import { useLanguage } from '../useLanguage';

const About = () => {
  const { t } = useLanguage();
  const skills = t.about.skills;

  const achievements = t.about.achievements.map((achievement, index) => ({
    icon: ['PROJ', 'TEAM', 'EXP', 'SAT'][index],
    ...achievement
  }));

  return (
    <section id="about" className="section-padding bg-white dark:bg-dark-900">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {t.about.title}
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            {t.about.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 p-6 rounded-xl border-l-4 border-primary-500 animate-fade-in">
                <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed font-medium">
                  "{t.about.quote}"
                </p>
              </div>
              
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                {t.about.description}
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center space-x-3 p-3 bg-white dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700 hover:shadow-md transition-shadow animate-slide-up" style={{animationDelay: '0.1s'}}>
                  <span className="text-green-500">✓</span>
                  <span className="text-gray-700 dark:text-gray-300">{t.about.formerCTO}</span>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-white dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700 hover:shadow-md transition-shadow animate-slide-up" style={{animationDelay: '0.2s'}}>
                  <span className="text-green-500">✓</span>
                  <span className="text-gray-700 dark:text-gray-300">{t.about.awsCertified}</span>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-white dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700 hover:shadow-md transition-shadow animate-slide-up" style={{animationDelay: '0.3s'}}>
                  <span className="text-green-500">✓</span>
                  <span className="text-gray-700 dark:text-gray-300">{t.about.fullStack}</span>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-white dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700 hover:shadow-md transition-shadow animate-slide-up" style={{animationDelay: '0.4s'}}>
                  <span className="text-green-500">✓</span>
                  <span className="text-gray-700 dark:text-gray-300">{t.about.aiExpert}</span>
                </div>
              </div>
            </div>

            {/* Skills */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{t.about.coreExpertise}</h3>
              {skills.map((skill) => (
                <div key={skill.name} className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-700 dark:text-gray-300">{skill.name}</span>
                    <span className="text-primary-600 dark:text-primary-400">{skill.level}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-dark-700 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-primary-500 to-secondary-500 h-2 rounded-full transition-all duration-1000"
                      style={{ width: `${skill.level}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>

            {/* LinkedIn */}
            <div className="flex items-center space-x-4 p-4 bg-secondary-50 dark:bg-secondary-900/20 rounded-lg hover:shadow-md transition-all duration-300">
              <div className="text-2xl font-bold text-secondary-600 dark:text-secondary-400">in</div>
              <div>
                <p className="text-gray-700 dark:text-gray-300">{t.about.connectProfessionally}</p>
                <a
                  href="https://www.linkedin.com/in/benjamin-juarez-4826862a/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-secondary-600 dark:text-secondary-400 hover:underline font-medium"
                >
                  {t.about.linkedinProfile}
                </a>
              </div>
            </div>
          </div>

          {/* Achievements */}
          <div className="grid grid-cols-2 gap-6">
            {achievements.map((achievement) => (
              <div key={achievement.title} className="card p-6 text-center hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group">
                <div className="text-2xl font-bold mb-4 text-primary-600 dark:text-primary-400 group-hover:scale-110 transition-transform">{achievement.icon}</div>
                <div className="text-3xl font-bold text-primary-600 dark:text-primary-400 mb-2">
                  {achievement.count}
                </div>
                <div className="text-gray-900 dark:text-white font-semibold mb-1">
                  {achievement.title}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {achievement.description}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;